import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { RefreshCw } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

export default function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, userRole, loading, signOut, checkSession } = useAuth();
  const location = useLocation();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Vérifier périodiquement le rôle de l'utilisateur
  useEffect(() => {
    if (user && userRole === 'guest') {
      const interval = setInterval(() => {
        checkSession();
      }, 10000); // Vérifier toutes les 10 secondes

      return () => clearInterval(interval);
    }
  }, [user, userRole, checkSession]);

  if (loading) {
    return <LoadingSpinner />;
  }

  // Rediriger vers la page de connexion si non authentifié
  if (!user || !userRole) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Rediriger les utilisateurs support vers la page des réservations
  if (userRole === 'support' && location.pathname === '/') {
    return <Navigate to="/bookings" replace />;
  }

  // Afficher le message d'attente pour les utilisateurs invités
  if (userRole === 'guest') {
    const handleRefresh = async () => {
      setIsRefreshing(true);
      try {
        await checkSession(); // Vérifier d'abord la session
        if (userRole === 'guest') { // Si toujours invité, se déconnecter
          await signOut();
        }
      } catch (error) {
        console.error('Error during refresh:', error);
      } finally {
        setIsRefreshing(false);
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-lg">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Compte en attente d'activation
            </h2>
            <p className="text-gray-600 mb-8">
              Votre compte doit être validé par un administrateur avant de pouvoir accéder à l'application.
            </p>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Vérification...' : 'Vérifier les accès'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Vérifier les rôles autorisés
  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-lg">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">
              Accès refusé
            </h2>
            <p className="text-gray-600">
              Vous n'avez pas les permissions nécessaires pour accéder à cette page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}